export type WorldPos = { wx: number; wy: number };

export class GridLayout {
  private tileSize = 16;

  private readonly minOffsetX: number;
  private offsetX: number;
  private offsetY: number;

  private hudReservePx: number;

  constructor(
    private readonly gridWidth: number,
    private readonly gridHeight: number,
    offsetX: number,
    offsetY: number,
    hudReservePx = 120
  ) {
    this.minOffsetX = offsetX;
    this.offsetX = offsetX;
    this.offsetY = offsetY;
    this.hudReservePx = hudReservePx;
  }

  /** Recompute tile size for the current viewport, then re-center the playfield horizontally. */
  recomputeLayout(viewWidth: number, viewHeight: number): void {
    const maxTileW = (viewWidth - this.minOffsetX * 2) / this.gridWidth;
    const maxTileH = (viewHeight - this.offsetY * 2 - this.hudReservePx) / this.gridHeight;
    this.tileSize = Math.floor(Math.max(8, Math.min(maxTileW, maxTileH)));

    // Center the board horizontally (but never smaller than the minimum padding).
    const playW = this.gridWidth * this.tileSize;
    const centeredX = Math.floor((viewWidth - playW) / 2);
    this.offsetX = Math.max(this.minOffsetX, centeredX);
  }

  setHudReservePx(px: number): void {
    this.hudReservePx = Math.max(0, Math.floor(px));
  }

  getHudReservePx(): number {
    return this.hudReservePx;
  }

  getTileSize(): number {
    return this.tileSize;
  }

  getOffsetX(): number {
    return this.offsetX;
  }

  getOffsetY(): number {
    return this.offsetY;
  }

  gridToWorld(x: number, y: number): WorldPos {
    const wx = this.offsetX + x * this.tileSize + this.tileSize / 2;
    const wy = this.offsetY + y * this.tileSize + this.tileSize / 2;
    return { wx, wy };
  }

  getPlayAreaSize(): { w: number; h: number } {
    return { w: this.gridWidth * this.tileSize, h: this.gridHeight * this.tileSize };
  }

  /** Top Y (in world pixels) immediately below the grid; good anchor for HUD. */
  getHudTopY(): number {
    return this.offsetY + this.gridHeight * this.tileSize;
  }
}
